/**
 * `liveReviewFilter` — funzione **pura** di filtro delle skill condivisa dal
 * `LiveReviewWizard`.
 *
 * Riproduce fedelmente la logica di filtro di `SkillAssessmentWizard`, ma
 * estratta in un modulo isolato e privo di qualsiasi dipendenza da React/hook o
 * da stato server: prende in input il catalogo di skill, le competenze, i
 * reparti e le professioni necessari a derivare gli insiemi di skill, più i
 * criteri attivi (ambito, tipologia, ricerca). Restituisce l'elenco filtrato e
 * ordinato per nome. Essendo pura, è testabile in isolamento (property test in
 * task 8.2).
 *
 * Rispetto al wizard di autovalutazione, qui l'ambito NON include le voci
 * dipendenti dallo stato di valutazione dell'utente (`mine`/`unrated`): la
 * review riguarda un candidato esterno, perciò gli ambiti sono soltanto "tutte
 * le skill", "per business unit" e "per professione" (vedi design.md >
 * Components and Interfaces > Frontend > LiveReviewWizard).
 *
 * Reparti e professioni non hanno skill dirette: l'insieme di skill si deriva
 * risalendo le competenze (reparto → competenze con quel `departmentId` →
 * `skillIds`; Job → `competenceIds` → `skillIds`), esattamente come nel wizard.
 */
import type { CompetenceDto, JobDto, SkillDto, SkillType } from '../shared/dto';

/**
 * Prefissi dei valori di ambito che puntano a un reparto o a una professione
 * specifici. Le voci generali (`all`) non hanno prefisso. Allineati ai prefissi
 * usati da `SkillAssessmentWizard`.
 */
export const DEPT_PREFIX = 'dept:';
export const JOB_PREFIX = 'job:';

/**
 * Valore del filtro di ambito:
 * - `'all'`: tutte le skill del catalogo;
 * - `dept:<id>`: le skill delle competenze del reparto scelto (business unit);
 * - `job:<id>`: le skill delle competenze richieste dalla professione scelta.
 */
export type ScopeFilter = 'all' | string;

/**
 * Valore del filtro per tipologia:
 * - `'all'`: qualsiasi tipologia;
 * - `SkillType`: solo le skill di quella tipologia (`knowledge`/`skill`/`technology`);
 * - `'none'`: solo le skill prive di tipologia (documenti legacy senza `type`).
 */
export type TypeFilter = 'all' | SkillType | 'none';

/** Criteri di filtro attivi applicati al catalogo di skill. */
export interface LiveReviewFilterCriteria {
  /** Ambito: tutte le skill, per business unit o per professione. */
  scope: ScopeFilter;
  /** Tipologia e-CF o "senza tipo". */
  type: TypeFilter;
  /** Testo di ricerca sul nome skill (match case-insensitive, substring). */
  search: string;
}

/** Cataloghi necessari a derivare gli insiemi di skill per ambito. */
export interface LiveReviewFilterCatalog {
  /** Catalogo completo delle skill da filtrare. */
  skills: readonly SkillDto[];
  /** Competenze: anello che collega le skill a reparti e professioni. */
  competences: readonly CompetenceDto[];
  /** Professioni (Job): forniscono le competenze richieste per l'ambito. */
  jobs: readonly JobDto[];
}

/** reparto → insieme delle skill (unione delle skill delle sue competenze). */
function buildSkillIdsByDepartment(
  competences: readonly CompetenceDto[],
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const c of competences) {
    if (c.departmentId === null) continue;
    const set = map.get(c.departmentId) ?? new Set<string>();
    for (const skillId of c.skillIds) set.add(skillId);
    map.set(c.departmentId, set);
  }
  return map;
}

/**
 * professione → insieme delle skill (unione delle skill delle competenze
 * richieste dal Job).
 */
function buildSkillIdsByJob(
  competences: readonly CompetenceDto[],
  jobs: readonly JobDto[],
): Map<string, Set<string>> {
  // competenceId → skill collegate.
  const skillIdsByCompetence = new Map<string, Set<string>>();
  for (const c of competences) {
    skillIdsByCompetence.set(c.id, new Set(c.skillIds));
  }

  const map = new Map<string, Set<string>>();
  for (const job of jobs) {
    const set = new Set<string>();
    for (const competenceId of job.competenceIds) {
      const skillsOfComp = skillIdsByCompetence.get(competenceId);
      if (skillsOfComp) for (const skillId of skillsOfComp) set.add(skillId);
    }
    map.set(job.id, set);
  }
  return map;
}

/**
 * Filtra il catalogo di skill applicando, nell'ordine, ambito → tipologia →
 * ricerca testuale sul nome, e restituisce il risultato ordinato per nome
 * (case-insensitive, locale `it`).
 *
 * Funzione pura: nessun effetto collaterale, nessuna dipendenza esterna. Dato lo
 * stesso input produce sempre lo stesso output.
 */
export function filterLiveReviewSkills(
  catalog: LiveReviewFilterCatalog,
  criteria: LiveReviewFilterCriteria,
): SkillDto[] {
  const { skills, competences, jobs } = catalog;
  const { scope, type, search } = criteria;

  let result: readonly SkillDto[] = skills;

  // 1) Ambito. Reparto/professione derivano le skill risalendo le competenze.
  if (scope.startsWith(DEPT_PREFIX)) {
    const skillIdsByDepartment = buildSkillIdsByDepartment(competences);
    const allowed = skillIdsByDepartment.get(scope.slice(DEPT_PREFIX.length));
    result = allowed ? result.filter((s) => allowed.has(s.id)) : [];
  } else if (scope.startsWith(JOB_PREFIX)) {
    const skillIdsByJob = buildSkillIdsByJob(competences, jobs);
    const allowed = skillIdsByJob.get(scope.slice(JOB_PREFIX.length));
    result = allowed ? result.filter((s) => allowed.has(s.id)) : [];
  }
  // `'all'`: nessun filtro di ambito.

  // 2) Tipologia. "Senza tipo" (`none`) seleziona le skill legacy prive di `type`.
  if (type !== 'all') {
    result = result.filter((s) =>
      type === 'none' ? s.type === undefined : s.type === type,
    );
  }

  // 3) Ricerca testuale sul nome (substring, case-insensitive).
  const query = search.trim().toLowerCase();
  if (query.length > 0) {
    result = result.filter((s) => s.name.toLowerCase().includes(query));
  }

  return [...result].sort((a, b) =>
    a.name.localeCompare(b.name, 'it', { sensitivity: 'base' }),
  );
}
